/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ry-n-split — LOCKED INVARIANTS                                            ║
 * ║                                                                            ║
 * ║  These six examples are the contract for this component. They are the      ║
 * ║  "convention over config" promise: the simple case is zero config, and     ║
 * ║  complexity is opt-in per pane. DO NOT change these examples or the API    ║
 * ║  they imply without EXPLICIT mutual agreement (Ryan + Claude). They are    ║
 * ║  what ships in the kitchen sink and what consumers rely on.                ║
 * ║                                                                            ║
 * ║  1 — Equal columns, zero config                                           ║
 * ║      <ry-n-split>                                                          ║
 * ║        <section>A</section><section>B</section><section>C</section>        ║
 * ║      </ry-n-split>                                                         ║
 * ║                                                                            ║
 * ║  2 — Resizable 3-column app body                                          ║
 * ║      <ry-n-split resizable>                                                ║
 * ║        <nav>Files</nav><main>Editor</main><aside>Inspector</aside>        ║
 * ║      </ry-n-split>                                                         ║
 * ║                                                                            ║
 * ║  3 — Fixed rails + flexible center (mixed)                                ║
 * ║      <ry-n-split resizable>                                                ║
 * ║        <nav size="240">Files</nav>                                         ║
 * ║        <main>Editor</main>                                                 ║
 * ║        <aside size="320">Inspector</aside>                                 ║
 * ║      </ry-n-split>                                                         ║
 * ║                                                                            ║
 * ║  4 — Full IDE, persisted (nested vertical × horizontal)                   ║
 * ║      <ry-n-split orientation="vertical" resizable persist="ide">          ║
 * ║        <ry-n-split resizable>                                              ║
 * ║          <nav size="220">Explorer</nav>                                    ║
 * ║          <main>Editor</main>                                               ║
 * ║          <aside size="300">Inspector</aside>                               ║
 * ║        </ry-n-split>                                                       ║
 * ║        <section size="180">Terminal</section>                              ║
 * ║      </ry-n-split>                                                         ║
 * ║                                                                            ║
 * ║  5 — Holy-grail app shell (header/footer locked, resizable middle)        ║
 * ║      <ry-n-split orientation="vertical">                                   ║
 * ║        <header size="56">Top bar</header>                                  ║
 * ║        <ry-n-split resizable>                                              ║
 * ║          <nav size="240">Nav</nav>                                         ║
 * ║          <main>Content</main>                                              ║
 * ║        </ry-n-split>                                                       ║
 * ║        <footer size="32">Status</footer>                                   ║
 * ║      </ry-n-split>                                                         ║
 * ║                                                                            ║
 * ║  6 — Collapsible sidebar (drag to edge → snaps shut)                      ║
 * ║      <ry-n-split resizable>                                                ║
 * ║        <nav size="240" min="0" collapsible>Files</nav>                     ║
 * ║        <main>Editor</main>                                                 ║
 * ║      </ry-n-split>                                                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Ported, not invented:
 *   • Split.js (MIT © Nathan Cahill) — the drag/snap/clamp math (`drag`,
 *     `adjust`, `calculateSizes`, `startDragging`/`stopDragging`) and the
 *     `calc(% - gutterpx)` fluid-sizing model. https://github.com/nathancahill/split
 *   • react-resizable-panels (MIT © Brian Vaughn) — the declarative API shape
 *     (group + bare panes, handles auto-inserted), per-pane min/collapsible,
 *     and persist-by-id semantics. https://github.com/bvaughn/react-resizable-panels
 * See LICENSE-THIRD-PARTY.md.
 */

/**
 * @component ry-n-split
 * @category  layout
 * @summary   N-pane resizable splitter (ry-ui-only; ports Split.js + react-resizable-panels). Direct children ARE the panes — no wrapper. Equal by default; `resizable` auto-inserts a drag handle between every adjacent pair (panes trade space, total preserved, stop-at-min clamp). Per-pane sizing/limits come from attributes on the child: `size` (px/%), `min`, `max`, `collapsible`. Works in both `[orientation]` axes, nests freely, and `persist="id"` remembers the layout.
 *
 * @attribute orientation  'horizontal' | 'vertical'   default='horizontal'   Split axis.
 * @attribute resizable    boolean   default=false     Auto-insert N-1 drag handles; enable mouse/touch/keyboard resize.
 * @attribute gap          'none' | 'sm' | 'md' | 'lg'  default=(1.5rem)      Gutter between panes. `none` = flush.
 * @attribute divided      boolean   default=false     Zero the gap + a 1px divider line between panes.
 * @attribute persist      string?                      localStorage key — saves/restores the size array (only with `resizable`).
 * @attribute snap         number    default=4         Snap-to-min range in px — dragging within this distance of a pane's `min` jumps to the min (Split.js `snapOffset`, retuned from its 30px default).
 *
 * @attribute (per child) size         string?   Initial size of that pane — `"240"`/`"240px"` (fixed) or `"30%"`. Unsized panes share the remainder equally.
 * @attribute (per child) min          string?   default=100   Minimum px the pane can shrink to while dragging.
 * @attribute (per child) max          string?   Maximum px the pane can grow to.
 * @attribute (per child) collapsible  boolean   Dragging near the edge snaps the pane shut (to its min, or 0 if min=0).
 *
 * @property  sizes  number[]   Read-only — current pane sizes in px along the axis.
 *
 * @event ry:resize
 *        detail: { sizes: number[] }
 *        fires:  on every drag move + commit. `sizes` is per-pane px along the axis.
 *
 * @keyboard  Focused handle: Arrow keys move that boundary (Left/Right horizontal, Up/Down vertical). Shift = 50px step, else 10px.
 *
 * @state     `data-ry-resizing` on the host during an active drag.
 *
 * @content   N direct children = N panes. Handles are inserted between them when `[resizable]`. Per-pane config lives on the child element; if a child consumes `size`/`min` itself, wrap it in a plain `<div size="…">`.
 *
 * @gotcha  Direct children ARE panes — no `<ry-pane>` wrapper. Per-pane attributes (`size`/`min`/`max`/`collapsible`) go on whatever the child is (`<nav>`, `<div>`, etc.).
 *
 * @gotcha  `size` is the INITIAL size; a resizable split still lets you drag past it. To lock a pane, set `min` = `max`. Unsized panes split the leftover space equally.
 *
 * @gotcha  Clamp is stop-at-min (the editor feel): a boundary won't push past a neighbour's `min`. `collapsible` lets a pane snap shut instead.
 *
 * @gotcha  `persist` stores an array of px sizes keyed by index. Changing the number/order of panes invalidates a saved layout (it's ignored if the count differs).
 *
 * @example equal-columns
 * ```html
 * <ry-n-split style="height:140px">
 *   <ry-card>A</ry-card><ry-card>B</ry-card><ry-card>C</ry-card>
 * </ry-n-split>
 * ```
 *
 * @example resizable-3-column
 * ```html
 * <ry-n-split resizable style="height:180px">
 *   <nav>Files</nav><main>Editor</main><aside>Inspector</aside>
 * </ry-n-split>
 * ```
 *
 * @example fixed-rails-flex-center
 * ```html
 * <ry-n-split resizable style="height:180px">
 *   <nav size="200">Files</nav>
 *   <main>Editor</main>
 *   <aside size="240">Inspector</aside>
 * </ry-n-split>
 * ```
 *
 * @example full-ide
 * ```html
 * <ry-n-split orientation="vertical" resizable persist="ide-demo" style="height:340px">
 *   <ry-n-split resizable>
 *     <nav size="200">Explorer</nav>
 *     <main>Editor</main>
 *     <aside size="240">Inspector</aside>
 *   </ry-n-split>
 *   <section size="120">Terminal</section>
 * </ry-n-split>
 * ```
 *
 * @example app-shell
 * ```html
 * <ry-n-split orientation="vertical" style="height:320px">
 *   <header size="48">Top bar</header>
 *   <ry-n-split resizable>
 *     <nav size="200">Nav</nav>
 *     <main>Content</main>
 *   </ry-n-split>
 *   <footer size="32">Status</footer>
 * </ry-n-split>
 * ```
 *
 * @example collapsible-sidebar
 * ```html
 * <ry-n-split resizable style="height:180px">
 *   <nav size="240" min="0" collapsible>Files</nav>
 *   <main>Editor</main>
 * </ry-n-split>
 * ```
 *
 * @tutorial equal-by-default
 * ```js
 * const el = mount('<ry-n-split><div>a</div><div>b</div><div>c</div></ry-n-split>');
 * // No size attrs → every pane gets an equal flex weight.
 * const panes = [...el.children].filter(c => c.getAttribute('data-ry-target') !== 'handle');
 * await expectEqual(panes.length, 3, 'three panes');
 * for (const p of panes) await expectEqual(p.style.flexGrow, '1', 'equal weight');
 * ```
 *
 * @tutorial resizable-inserts-n-minus-1-handles
 * ```js
 * const el = mount('<ry-n-split resizable><div>a</div><div>b</div><div>c</div></ry-n-split>');
 * const handles = el.querySelectorAll('[data-ry-target="handle"]');
 * await expectEqual(handles.length, 2, 'N-1 handles for N=3 panes');
 * ```
 *
 * @tutorial no-handles-without-resizable
 * ```js
 * const el = mount('<ry-n-split><div>a</div><div>b</div></ry-n-split>');
 * await expectEqual(el.querySelector('[data-ry-target="handle"]'), null, 'static: no handles');
 * ```
 *
 * @tutorial fixed-pane-gets-px-basis
 * ```js
 * const el = mount('<ry-n-split><nav size="240">n</nav><main>m</main></ry-n-split>');
 * const nav = el.querySelector('nav');
 * await expectEqual(nav.style.flexGrow, '0', 'fixed pane does not grow');
 * await expectEqual(nav.style.flexBasis, '240px', 'fixed pane px basis');
 * ```
 *
 * @tutorial percent-size-passthrough
 * ```js
 * const el = mount('<ry-n-split><div size="30%">a</div><div>b</div></ry-n-split>');
 * await expectEqual(el.querySelector('div').style.flexBasis, '30%', 'percent basis');
 * ```
 *
 * @tutorial orientation-handle-aria
 * ```js
 * const h = mount('<ry-n-split resizable><div>a</div><div>b</div></ry-n-split>');
 * await expectAttr(h.querySelector('[data-ry-target="handle"]'), 'aria-orientation', 'vertical');
 * const v = mount('<ry-n-split resizable orientation="vertical"><div>a</div><div>b</div></ry-n-split>');
 * await expectAttr(v.querySelector('[data-ry-target="handle"]'), 'aria-orientation', 'horizontal');
 * ```
 *
 * @see ry-split
 */

import { RyElement } from '../core/ry-element.js';

interface Pane {
  el: HTMLElement;
  /** Fixed px basis (number) or percentage string, or null = flexible/equal. */
  size: number | string | null;
  min: number;
  max: number;
  collapsible: boolean;
  grow: number;
}

const IS_NUMBER = /^[+-]?\d*\.?\d+$/;
const IS_PERCENT = /^[+-]?\d*\.?\d+%$/;

export class RyNSplit extends RyElement {
  static get observedAttributes(): string[] {
    return ['orientation', 'ratio'];
  }

  #panes: Pane[] = [];
  #handles: HTMLElement[] = [];

  setup(): void {
    this.#readPanes();
    this.#layout();
    if (this.hasAttribute('resizable')) {
      this.#restore();
      this.#insertHandles();
    }
  }

  attributeChangedCallback(_n: string, oldV: string | null, newV: string | null): void {
    if (oldV === newV || !this.hasAttribute('data-ry-initialized')) return;
    this.#readPanes();
    this.#layout();
    for (const h of this.#handles) {
      h.setAttribute('aria-orientation', this.#vertical() ? 'horizontal' : 'vertical');
    }
  }

  get sizes(): number[] {
    return this.#panes.map((p) => this.#px(p.el));
  }

  // ── Read declarative config off the bare children ──
  #readPanes(): void {
    const ratio = (this.getAttribute('ratio') ?? '')
      .split(/[/:]/)
      .map((n) => parseFloat(n.trim()))
      .filter((n) => Number.isFinite(n));

    this.#panes = [...this.children]
      .filter((c) => c.getAttribute('data-ry-target') !== 'handle')
      .map((c, i) => {
        const el = c as HTMLElement;
        el.setAttribute('data-ry-target', 'pane');
        const sizeAttr = el.getAttribute('size');
        let size: number | string | null = null;
        if (sizeAttr != null && sizeAttr !== '') {
          const raw = sizeAttr.trim();
          if (IS_PERCENT.test(raw)) size = raw;
          else if (IS_NUMBER.test(raw)) size = parseFloat(raw);
          else size = raw; // e.g. "240px"
        }
        const minRaw = el.getAttribute('min');
        const maxRaw = el.getAttribute('max');
        return {
          el,
          size,
          min: minRaw != null ? parseFloat(minRaw) : 100,
          max: maxRaw != null ? parseFloat(maxRaw) : Infinity,
          collapsible: el.hasAttribute('collapsible'),
          grow: Number.isFinite(ratio[i]!) ? ratio[i]! : 1,
        };
      });
  }

  // ── Apply the flex layout (Split.js's element-style model adapted to flex) ──
  // Fixed panes: flex 0 0 <size>. Flexible panes: flex <grow> 1 0 (share rest).
  #layout(): void {
    for (const p of this.#panes) {
      p.el.style.minWidth = '0';
      p.el.style.minHeight = '0';
      if (p.size == null) {
        p.el.style.flex = `${p.grow} 1 0`;
        p.el.style.flexBasis = '0';
      } else if (typeof p.size === 'number') {
        p.el.style.flex = '0 0 auto';
        p.el.style.flexBasis = `${p.size}px`;
      } else {
        p.el.style.flex = '0 0 auto';
        p.el.style.flexBasis = p.size; // "30%" or "240px"
      }
    }
  }

  #vertical(): boolean {
    return this.getAttribute('orientation') === 'vertical';
  }

  #px(el: HTMLElement): number {
    const r = el.getBoundingClientRect();
    return this.#vertical() ? r.height : r.width;
  }

  // ── Handles (one between each adjacent pair) ──
  #insertHandles(): void {
    for (let i = 1; i < this.#panes.length; i++) {
      const handle = document.createElement('div');
      handle.setAttribute('data-ry-target', 'handle');
      handle.className = 'ry-n-split__handle';
      handle.setAttribute('role', 'separator');
      handle.setAttribute('aria-orientation', this.#vertical() ? 'horizontal' : 'vertical');
      handle.setAttribute('tabindex', '0');
      this.insertBefore(handle, this.#panes[i]!.el);
      this.#handles.push(handle);
      this.on(handle, 'mousedown', (e) => this.#startDrag(e as MouseEvent, i - 1, i, handle));
      this.on(handle, 'touchstart', ((e: TouchEvent) => this.#startDrag(e, i - 1, i, handle)) as EventListener);
      this.on(handle, 'keydown', (e) => this.#onKey(e as KeyboardEvent, i - 1, i));
    }
  }

  // ── Drag: ported from Split.js (drag/adjust/calculateSizes/start/stop) ──
  // Each handle owns the pair (a, b). Dragging redistributes between exactly
  // those two; total preserved; clamped to each pane's min (stop-at-min) and
  // max, with collapsible snap-to-edge.
  #startDrag(e: MouseEvent | TouchEvent, ai: number, bi: number, handle: HTMLElement): void {
    if ('button' in e && e.button !== 0) return;
    e.preventDefault();
    const a = this.#panes[ai]!;
    const b = this.#panes[bi]!;
    const vertical = this.#vertical();
    const pos = this.#mousePos(e);

    // calculateSizes: the pair's combined size + a's leading edge.
    const aRect = a.el.getBoundingClientRect();
    const bRect = b.el.getBoundingClientRect();
    const aSize0 = vertical ? aRect.height : aRect.width;
    const bSize0 = vertical ? bRect.height : bRect.width;
    const pairSize = aSize0 + bSize0;
    const start = vertical ? aRect.top : aRect.left;
    const dragOffset = pos - (vertical ? aRect.bottom : aRect.right);
    const snap = parseFloat(this.getAttribute('snap') ?? '4');

    this.setAttribute('data-ry-resizing', '');
    handle.classList.add('is-dragging'); // highlight THIS bar only, not all of them
    document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    const move = (ev: MouseEvent | TouchEvent) => {
      // Split.js drag(): offset = mouse - start (+gutter/dragOffset accounting).
      let offset = this.#mousePos(ev) - start + -dragOffset;
      // Stop-at-min clamp (Split.js snap logic): a >= aMin, b >= bMin.
      if (offset <= a.min + (a.collapsible ? 0 : snap)) {
        offset = a.collapsible && offset < a.min / 2 ? 0 : a.min;
      } else if (offset >= pairSize - (b.min + (b.collapsible ? 0 : snap))) {
        offset = b.collapsible && pairSize - offset < b.min / 2 ? pairSize : pairSize - b.min;
      }
      // Max clamp.
      if (offset > a.max) offset = a.max;
      else if (pairSize - offset > b.max) offset = pairSize - b.max;

      // adjust(): a = offset, b = remainder. Pin both to px (flex 0 0).
      a.el.style.flex = '0 0 auto';
      a.el.style.flexBasis = `${offset}px`;
      b.el.style.flex = '0 0 auto';
      b.el.style.flexBasis = `${pairSize - offset}px`;
      a.size = offset;
      b.size = pairSize - offset;
      this.emit('resize', { sizes: this.sizes });
    };
    const stop = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', stop);
      this.removeAttribute('data-ry-resizing');
      handle.classList.remove('is-dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.#persist();
      this.emit('resize', { sizes: this.sizes });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', stop);
  }

  #mousePos(e: MouseEvent | TouchEvent): number {
    const vertical = this.#vertical();
    if ('touches' in e && e.touches[0]) return vertical ? e.touches[0].clientY : e.touches[0].clientX;
    const m = e as MouseEvent;
    return vertical ? m.clientY : m.clientX;
  }

  #onKey(e: KeyboardEvent, ai: number, bi: number): void {
    const vertical = this.#vertical();
    const grow = vertical ? 'ArrowDown' : 'ArrowRight';
    const shrink = vertical ? 'ArrowUp' : 'ArrowLeft';
    if (e.key !== grow && e.key !== shrink) return;
    e.preventDefault();
    const a = this.#panes[ai]!;
    const b = this.#panes[bi]!;
    const step = (e.shiftKey ? 50 : 10) * (e.key === grow ? 1 : -1);
    const aSize = this.#px(a.el);
    const bSize = this.#px(b.el);
    const pair = aSize + bSize;
    let offset = Math.max(a.min, Math.min(pair - b.min, aSize + step));
    if (offset > a.max) offset = a.max;
    a.el.style.flex = '0 0 auto'; a.el.style.flexBasis = `${offset}px`;
    b.el.style.flex = '0 0 auto'; b.el.style.flexBasis = `${pair - offset}px`;
    this.#persist();
    this.emit('resize', { sizes: this.sizes });
  }

  // ── Persistence (react-resizable-panels: save the size array by id) ──
  #key(): string | null {
    const k = this.getAttribute('persist');
    return k ? `ry-n-split:${k}` : null;
  }

  #persist(): void {
    const k = this.#key();
    if (!k) return;
    try { localStorage.setItem(k, JSON.stringify(this.sizes.map(Math.round))); } catch { /* */ }
  }

  #restore(): void {
    const k = this.#key();
    if (!k) return;
    try {
      const saved = localStorage.getItem(k);
      if (!saved) return;
      const arr = JSON.parse(saved) as number[];
      if (!Array.isArray(arr) || arr.length !== this.#panes.length) return;
      this.#panes.forEach((p, i) => {
        p.el.style.flex = '0 0 auto';
        p.el.style.flexBasis = `${arr[i]}px`;
      });
    } catch { /* */ }
  }
}

customElements.define('ry-n-split', RyNSplit);
